import { EVENT, CLIENT_EVENTS } from '@essential-support/shared/constants';
import { isPrerendering, whenActivated } from './prerender.js';

// Client usage beacon (telemetry T4). Buffers the UI events the server can't
// observe and ships them to /api/activity in batches via sendBeacon — so it never
// blocks rendering and survives page unload. Deliberately neutral naming (module
// "activity", endpoint "/api/activity" — never "analytics"/"track"/"metrics") so a
// content blocker can't recognize + block it. FIRE-AND-FORGET: every failure is a
// silent no-op; a blocked or broken beacon must never affect the app. The server
// re-validates every event against CLIENT_EVENTS and stamps the actor, so this is
// convenience + batching, never the source of truth.
// PRERENDER (speculation rules — lib/prerender.js): a prerendered portal may never be shown,
// so nothing is sent while it's hidden. Events recorded meanwhile (the first page.viewed)
// are held — unstamped — and enqueued on activation, stamped with the moment the visitor
// actually saw the page; a discarded prerender drops them with the document.

const ALLOWED = new Set(CLIENT_EVENTS);
const FLUSH_AT = 10;      // send once this many events are buffered
const IDLE_MS = 15000;    // ...or after this much quiet
const MAX_BUFFER = 50;    // hard cap (matches the server's per-batch limit)

let buffer = [];
let held = [];            // recorded while prerendering; enqueued on activation
let idleTimer = null;
let started = false;

function flush() {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
  if (!buffer.length || isPrerendering()) return;
  const batch = buffer.slice(0, MAX_BUFFER);
  buffer = [];
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/activity', new Blob([body], { type: 'application/json' }));
    } else {
      fetch('/api/activity', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body, keepalive: true, credentials: 'same-origin'
      }).catch(() => {});
    }
  } catch { /* never throw */ }
}

function enqueue(entry) {
  if (!started || !ALLOWED.has(entry.event)) return; // client-side guard (server also enforces)
  if (isPrerendering()) { if (held.length < MAX_BUFFER) held.push(entry); return; }
  if (buffer.length >= MAX_BUFFER) return;            // drop rather than grow unbounded
  buffer.push({ occurredAt: new Date().toISOString(), ...entry });
  if (buffer.length >= FLUSH_AT) return flush();
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(flush, IDLE_MS);
}

// Record a UI event. `props` is optional; keep it tiny (ids + enums only).
export function record(event, props) {
  enqueue(props ? { event, props } : { event });
}

// Ticket-scoped variant — ticketId rides top-level (a first-class join key), not props.
export function recordTicket(event, ticketId, props) {
  enqueue(props ? { event, ticketId, props } : { event, ticketId });
}

export function init() {
  if (started || typeof window === 'undefined') return;
  started = true;
  whenActivated().then(() => { const early = held; held = []; early.forEach(enqueue); });
  // Flush on the reliable "session paused/over" signals.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
  // Client-error stopgap (ES has no Sentry yet): capture uncaught errors + rejections.
  window.addEventListener('error', (e) => {
    record(EVENT.APP_ERROR, { message: String(e?.message || 'error').slice(0, 300) });
  });
  window.addEventListener('unhandledrejection', (e) => {
    record(EVENT.APP_ERROR, { message: String(e?.reason?.message || e?.reason || 'rejection').slice(0, 300) });
  });
}
